import { useMemo } from "react";
import type { ComplianceSummary, TeamRollup } from "../types";
import { windowLong, windowShort } from "../lib/reportingWindow";

interface Props {
  summary: ComplianceSummary;
}

const BENCHMARK = 80;  // window target

// Colors keyed to the SAME benchmark the dashed target line uses
// (matching MonthEndChart / KpiCards): at-or-above target is green,
// within 10 points is amber, further below is red. A bar that crosses
// the dashed line must never render in a warning color.
function bgForPct(pct: number): string {
  if (pct >= BENCHMARK) return "bg-emerald-500";
  if (pct >= BENCHMARK - 10) return "bg-amber-400";
  return "bg-red-600";
}

function textForPct(pct: number): string {
  if (pct >= BENCHMARK) return "text-emerald-700";
  if (pct >= BENCHMARK - 10) return "text-amber-700";
  return "text-red-700";
}

export default function TeamOrgChart({ summary }: Props) {
  // Best-first: highest YTD compliance at the top.
  const teams = useMemo(
    () => [...summary.teams].sort((a, b) => b.ytd_pct - a.ytd_pct),
    [summary.teams],
  );

  if (teams.length === 0) return null;

  const totalPeople = teams.reduce((acc, t) => acc + t.employee_count, 0);
  // Prefer the headline KPI from the API — it's a true person-week
  // weighted % with no_entries excluded from the denominator. Fall back
  // to a per-team-size weighted avg of ytd_pct for older payloads.
  const companyYtd =
    typeof summary.kpis.company_ytd_pct === "number"
      ? Math.round(10 * summary.kpis.company_ytd_pct) / 10
      : totalPeople > 0
      ? Math.round(
          (10 * teams.reduce((acc, t) => acc + t.ytd_pct * t.employee_count, 0)) /
            totalPeople,
        ) / 10
      : 0;

  return (
    <section className="rounded-2xl bg-white shadow-card border border-slate-200/70 overflow-hidden">
      <header className="px-5 py-4 border-b border-slate-100 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-bold tracking-tight text-rancho-ink">
            Compliance by team · {windowShort(summary)}
          </h2>
          <p className="text-xs text-rancho-slate mt-0.5">
            Sorted best-first. Bar = % of person-weeks fully compliant{" "}
            {windowLong(summary)} (on time AND no month-end miss). Dashed
            line marks the {BENCHMARK}% target. Click a team to jump to it
            in the timeliness chart below.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.15em] text-rancho-slate">
            Company · {windowShort(summary)}
          </div>
          <div className={`text-2xl font-extrabold ${textForPct(companyYtd)}`}>
            {companyYtd.toFixed(1)}%
          </div>
          <div className="text-xs text-rancho-slate">
            {totalPeople} people · weighted avg
          </div>
        </div>
      </header>

      <div className="px-5 py-5 space-y-2">
        {teams.map((t) => (
          <TeamBar key={t.team_name} team={t} windowLabel={windowShort(summary)} />
        ))}
        <BenchmarkLegend />
      </div>
    </section>
  );
}

function TeamBar({ team, windowLabel }: { team: TeamRollup; windowLabel: string }) {
  const pct = team.ytd_pct;
  const widthPct = Math.max(2, pct);

  function onClick() {
    window.dispatchEvent(
      new CustomEvent("scrollToTeam", { detail: { team: team.team_name } }),
    );
  }

  // Show the % at the right edge of the FILLED portion, on top of the
  // colored bar. When the bar is short, the label sits just past the
  // bar's right edge in a colored chip so it's always legible.
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 text-left rounded-md px-1 py-1 -mx-1 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-rancho-orange/40 transition-colors"
      title={`${team.team_name} · ${pct.toFixed(1)}% ${windowLabel} · ${team.employee_count} people · click to jump to this team in the timeliness chart`}
    >
      <div className="w-64 text-sm font-medium text-rancho-ink shrink-0 leading-tight">
        {team.team_name}
        <span className="ml-1 text-xs text-rancho-slate font-normal">
          ({team.employee_count})
        </span>
      </div>
      <div className="flex-1 h-7 bg-slate-100 rounded-md overflow-hidden relative">
        {/* Filled portion */}
        <div
          className={`h-full ${bgForPct(pct)} transition-all flex items-center justify-end pr-2`}
          style={{ width: `${widthPct}%` }}
        >
          {pct >= 12 && (
            <span className="text-xs font-bold text-white tabular-nums">
              {pct.toFixed(1)}%
            </span>
          )}
        </div>
        {/* Off-bar label when the bar is too short to host the text */}
        {pct < 12 && (
          <div
            className="absolute top-0 h-full flex items-center"
            style={{ left: `calc(${widthPct}% + 6px)` }}
          >
            <span className={`text-xs font-bold tabular-nums ${textForPct(pct)}`}>
              {pct.toFixed(1)}%
            </span>
          </div>
        )}
        {/* 80% benchmark line */}
        <div
          className="absolute top-0 bottom-0 border-l-2 border-dashed border-slate-700/60"
          style={{ left: `${BENCHMARK}%` }}
          aria-hidden
        />
      </div>
    </button>
  );
}

function BenchmarkLegend() {
  return (
    <div className="pt-3 flex items-center gap-2 text-xs text-rancho-slate">
      <span className="inline-block w-6 border-t-2 border-dashed border-slate-700/60" />
      <span>{BENCHMARK}% benchmark</span>
    </div>
  );
}
